// ── ReferralSheet — suggest a profile to one of your connections ──────────
// Friend graph = bidirectional friend_ratings:
//   • people who rated the current user (rater_id IS NOT NULL)
//   • people the current user rated
// This naturally captures raters who later signed up as members.
import { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';

export default function ReferralSheet({ profile, onClose }) {
  const { currentUser, lang } = useApp();
  const fr = lang === 'fr';

  const [friends,  setFriends]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [message,  setMessage]  = useState('');
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [updated,   setUpdated]   = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    if (!currentUser?.id) return;
    import('../utils/supabase').then(async ({ supabase }) => {
      // Both sides of the friend_ratings relationship in parallel
      const [{ data: ratedMe }, { data: iRated }] = await Promise.all([
        supabase
          .from('friend_ratings')
          .select('rater_id')
          .eq('target_id', currentUser.id)
          .not('rater_id', 'is', null),
        supabase
          .from('friend_ratings')
          .select('target_id')
          .eq('rater_id', currentUser.id),
      ]);

      const ids = [...new Set([
        ...(ratedMe || []).map(r => r.rater_id),
        ...(iRated  || []).map(r => r.target_id),
      ].filter(id => id && id !== profile?.id && id !== currentUser.id))];

      if (!ids.length) { setLoading(false); return; }

      const { data: profiles } = await supabase
        .from('users')
        .select('id, name, age, photos, job')
        .in('id', ids);

      setFriends(profiles || []);
      setLoading(false);
    });
  }, [currentUser?.id, profile?.id]);

  const selectedFriend = friends.find(f => f.id === selected);

  const send = async () => {
    if (!selected || !currentUser?.id) return;
    setSending(true);
    setError(null);
    try {
      const { supabase } = await import('../utils/supabase');
      const newMsg = message.trim() || null;

      // Check if this exact suggestion was already sent
      const { data: existing } = await supabase
        .from('referrals')
        .select('id, message')
        .eq('referrer_id', currentUser.id)
        .eq('referred_id', selected)
        .eq('profile_id', profile.id)
        .maybeSingle();

      if (existing) {
        // Append new message to the old one and reset date so it surfaces at the top
        const combined = [existing.message, newMsg].filter(Boolean).join('\n\n—\n\n');
        const { error: err } = await supabase
          .from('referrals')
          .update({
            message    : combined || null,
            created_at : new Date().toISOString(),
            seen_at    : null,
          })
          .eq('id', existing.id);
        if (err) throw err;
        setUpdated(true);
      } else {
        const { error: err } = await supabase
          .from('referrals')
          .insert({
            referrer_id : currentUser.id,
            referred_id : selected,
            profile_id  : profile.id,
            message     : newMsg,
          });
        if (err) throw err;
      }
      setSent(true);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxHeight:'78vh' }}>

        {/* Handle */}
        <div style={{ textAlign:'center', padding:'0.6rem 0 0' }}>
          <div style={{ width:32, height:2, background:'var(--muted2)', margin:'0 auto', borderRadius:99 }}/>
        </div>

        <div style={{ padding:'1.2rem 1.2rem 1.8rem', overflowY:'auto' }}>

          {/* ── Success ── */}
          {sent ? (
            <div style={{ textAlign:'center', padding:'2rem 0' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'3rem', color:'var(--gold)', marginBottom:'0.6rem' }}>✦</div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, marginBottom:'0.5rem' }}>
                {updated
                  ? (fr ? 'Suggestion mise à jour' : 'Suggestion updated')
                  : (fr ? 'Suggestion envoyée'     : 'Suggestion sent')}
              </h3>
              <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.6 }}>
                {updated
                  ? (fr
                      ? `Ton message a été ajouté à la suggestion déjà envoyée à ${selectedFriend?.name}.`
                      : `Your message was added to the existing suggestion for ${selectedFriend?.name}.`)
                  : (fr
                      ? `${selectedFriend?.name} recevra ta suggestion de profil.`
                      : `${selectedFriend?.name} will receive your profile suggestion.`)}
              </p>
              <button onClick={onClose} className="btn btn-outline btn-md" style={{ marginTop:'1.2rem' }}>
                {fr ? 'Fermer' : 'Close'}
              </button>
            </div>

          ) : (
            <>
              {/* ── Profile header ── */}
              <div style={{ display:'flex', alignItems:'center', gap:'0.8rem', marginBottom:'1.1rem', paddingBottom:'1rem', borderBottom:'1px solid var(--border)' }}>
                {profile.photos?.[0] ? (
                  <div style={{ width:48, height:48, borderRadius:'var(--r-sm)', overflow:'hidden', flexShrink:0 }}>
                    <img src={profile.photos[0]} alt={profile.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  </div>
                ) : (
                  <div style={{ width:48, height:48, borderRadius:'var(--r-sm)', background:'var(--mid)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--muted)', fontSize:'1.2rem' }}>
                    {profile.name?.[0]}
                  </div>
                )}
                <div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', color:'var(--white)', marginBottom:'0.15rem' }}>
                    {fr ? 'Suggérer' : 'Suggest'}{' '}
                    <strong>{profile.name}</strong>
                    {profile.age ? `, ${profile.age}` : ''}
                  </div>
                  <div style={{ fontSize:'0.68rem', color:'var(--muted)' }}>
                    {fr
                      ? 'Choisis un·e ami·e à qui suggérer ce profil'
                      : 'Choose a friend to suggest this profile to'}
                  </div>
                </div>
              </div>

              {/* ── Loading ── */}
              {loading ? (
                <p style={{ textAlign:'center', color:'var(--muted)', fontSize:'0.82rem', padding:'1.5rem 0' }}>
                  {fr ? 'Chargement…' : 'Loading…'}
                </p>

              ) : friends.length === 0 ? (
                /* ── No friends yet ── */
                <div style={{ textAlign:'center', padding:'1rem 0' }}>
                  <p style={{ color:'var(--muted)', fontSize:'0.82rem', lineHeight:1.65, marginBottom:'0.8rem' }}>
                    {fr
                      ? 'Aucun de tes amis qui t\'ont noté n\'est encore inscrit sur BeeLove.'
                      : 'None of your friend raters have signed up on BeeLove yet.'}
                  </p>
                  <p style={{ color:'var(--muted2)', fontSize:'0.75rem', lineHeight:1.55 }}>
                    {fr
                      ? 'Invite-les à te noter — ceux qui s\'inscriront apparaîtront ici.'
                      : 'Invite them to rate you — those who sign up will appear here.'}
                  </p>
                </div>

              ) : (
                <>
                  {/* ── Friend list ── */}
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', maxHeight:220, overflowY:'auto', marginBottom:'1rem' }}>
                    {friends.map(f => (
                      <button
                        key={f.id}
                        onClick={() => setSelected(f.id === selected ? null : f.id)}
                        style={{
                          display:'flex', alignItems:'center', gap:'0.7rem',
                          padding:'0.65rem 0.8rem', textAlign:'left', width:'100%',
                          background: selected === f.id ? 'var(--gold-pale)' : 'var(--dark)',
                          border:`1px solid ${selected === f.id ? 'var(--gold-border)' : 'var(--border)'}`,
                          borderRadius:'var(--r-md)', cursor:'pointer', transition:'all 0.15s',
                        }}
                      >
                        <div className="avatar av-sm av-round" style={{ flexShrink:0, background:'var(--mid)' }}>
                          {f.photos?.[0]
                            ? <img src={f.photos[0]} alt={f.name}/>
                            : <span style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', fontSize:'0.85rem', color:'var(--muted)' }}>{f.name?.[0]}</span>}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'0.85rem', color:'var(--white)', fontWeight:500 }}>{f.name}</div>
                          {f.job && (
                            <div style={{ fontSize:'0.65rem', color:'var(--muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.job}</div>
                          )}
                        </div>
                        {selected === f.id && (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* ── Optional message (appears after selection) ── */}
                  {selected && (
                    <div style={{ marginBottom:'1rem' }}>
                      <p style={{ margin:'0 0 0.4rem', fontSize:'0.65rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em' }}>
                        {fr ? 'Message (optionnel)' : 'Message (optional)'}
                      </p>
                      <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder={fr
                          ? 'Je pense que vous vous entendriez bien…'
                          : 'I think you two would get along well…'}
                        rows={2}
                        style={{ width:'100%', padding:'0.65rem 0.75rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.83rem', resize:'none', outline:'none', boxSizing:'border-box' }}
                      />
                    </div>
                  )}

                  {error && (
                    <p style={{ fontSize:'0.75rem', color:'var(--red)', marginBottom:'0.7rem' }}>{error}</p>
                  )}

                  <button
                    onClick={send}
                    disabled={!selected || sending}
                    className="btn btn-gold btn-md btn-full"
                    style={{ opacity: selected ? 1 : 0.4 }}
                  >
                    {sending
                      ? (fr ? 'Envoi…' : 'Sending…')
                      : (fr ? 'Envoyer la suggestion' : 'Send suggestion')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
